import TablePagination from '@/components/Table/TablePagination';
import { GREY, PRIMARY } from '@/constants/color';
import { CircularProgress } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';

interface CustomChartOptions extends ChartOptions<'bar'> {
  maxBarThickness?: number;
  borderRadius?: number;
  borderSkipped?: boolean;
}

interface IProps {
  chartData: ChartData;
  loading?: boolean;
}

const HorizontalChart = ({ chartData, loading }: IProps) => {
  //#region pagination
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const start = (page - 1) * rowsPerPage;
  const end = start + rowsPerPage;
  const { datasets, labels } = chartData;
  const allDataValues = datasets?.[0]?.data || [];

  const processedLabels = useMemo(() => labels?.slice(start, end) || [], [labels, start, end]);
  const processedData = useMemo(() => datasets[0]?.data?.slice(start, end) || [], [datasets, start, end]);

  const handleChangePage = useCallback(
    (_: ChangeEvent<HTMLInputElement>, newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
    },
    [setRowsPerPage],
  );

  const total = labels?.length || 0;
  const totalPages = Math.ceil(total / rowsPerPage);

  const displayedStart = total ? start + 1 : 0;
  const displayedEnd = Math.min(end, total);

  const slicedChartData = {
    labels: processedLabels,
    datasets: [{ ...datasets, label: 'Conversions', data: processedData }],
  };

  const height = processedLabels.length * 75;
  const suggestedMax = Math.ceil(Math.max(...(allDataValues as number[])) * 1.05);
  //#endregion

  const ticksCb = useCallback(
    (index: number | string) => {
      const i = +index + start;
      const labels = chartData?.labels as string[];
      const label = labels?.[i];
      if (label && label.length > 40) return `${labels?.[i]?.slice(0, 40)}...`;
      return label;
    },
    [chartData.labels, start],
  );

  //#region options
  const options: CustomChartOptions = useMemo(
    () => ({
      plugins: {
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'right',
          offset: 0,
          color: GREY,
          textAlign: 'start',
          formatter: value => (+value).toLocaleString('en-US', { maximumFractionDigits: 0 }),
          font: {
            family: 'Plus Jakarta Sans',
            size: 12,
            weight: 500,
          },
        },
        legend: {
          display: true,
          align: 'start',
          labels: {
            boxHeight: 12,
            boxWidth: 12,
            padding: 0,
          },
        },
      },
      indexAxis: 'y',
      maxBarThickness: 30,
      borderRadius: 5,
      borderSkipped: false,
      backgroundColor: PRIMARY,
      scales: {
        y: {
          border: {
            display: false,
          },
          grid: {
            display: false,
            drawTicks: false,
          },
          ticks: {
            color: '#2E2C34',
            display: true,
            autoSkip: false,
            align: 'end',
            mirror: true,
            padding: 0,
            callback: ticksCb,
            labelOffset: -17,
            font: {
              family: 'Manrope',
              size: 14,
            },
          },
        },
        x: {
          max: suggestedMax,
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      maintainAspectRatio: false,
    }),
    [ticksCb, suggestedMax],
  );
  //#endregion

  if (!loading && !processedLabels?.length) return null;

  return (
    <div className='flex flex-col w-full'>
      <div className='horizontal_chart w-full'>
        {loading ? (
          <div className='flex w-full justify-center'>
            <CircularProgress />
          </div>
        ) : (
          <Bar style={{ height }} options={options} data={slicedChartData} />
        )}
      </div>
      <TablePagination
        sx={{
          padding: 0,
          marginTop: '8px',
        }}
        page={page}
        totalPages={totalPages}
        start={displayedStart}
        end={displayedEnd}
        total={labels?.length || 0}
        rowsPerPageOptions={[5, 10, 25]}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default HorizontalChart;
